import Page from "~/components/Page";
import Container from "~/components/Container";
import Section from "~/components/Section";
import {createAsync, useAction, useNavigate, useParams, useSubmission} from "@solidjs/router";
import {Show, Suspense} from "solid-js";
import {getClass} from "~/sdk/class";
import {useMember} from "~/context/MemberContext";
import {Button, LinkButton} from "~/components/ui/Button";
import {formatDate, subHours} from "date-fns";
import css from "./class.module.css";
import {createBooking} from "~/sdk/booking";
import {ApiError} from "~/sdk/error";

export default function Class() {

    const params = useParams()
    const member = useMember()

    const cls = createAsync(() => getClass(params.id))

    const createBookingAction = useAction(createBooking)
    const navigate = useNavigate()

    const hasClassPass = () => {
        return (member()?.classPasses ?? 0) > 0
    }

    const hasBooked = () => {
        return !!member()?.bookings.find(b => b?.class?.id === cls()?.id && !b?.canceledAt)
    }

    const isInPast = () => {
        const now = new Date()
        return new Date(cls()?.startsAt ?? 0) < now
    }

    const isTooLate = () => {
        const now = new Date()
        return subHours(new Date(cls()?.startsAt ?? 0), 1) <= now
    }

    const isChristmasClass = () => {
        return cls()?.id === "cDXdYZm7pFYYG4Eus0T9l"
    }

    const notBookable = () => {
        return submission.pending || isChristmasClass() || !hasClassPass() || hasBooked() || isInPast() || isTooLate()
    }

    const isCanceled = () => {
        return !!cls()?.canceledAt
    }

    const notBookableReason = () => {
        if (isChristmasClass()) {
            return "This class is fully booked."
        }
        if (isCanceled()) {
            return "This class is unavailable to book."
        }
        if (isInPast()) {
            return "This class has already taken place."
        }
        if (hasBooked()) {
            return "You have already booked this class."
        }
        if (isTooLate()) {
            return "Booking for this class is closed."
        }
        if (!hasClassPass()) {
            return "You do not have any class passes remaining."
        }
    }

    const submission = useSubmission(createBooking)

    const book = async () => {
        try {
            await createBookingAction({classId: cls()?.id!, memberId: member()?.id!})
            navigate("/booking/success")
        } catch (e) {
            throw new ApiError("Failed to book class", e)
        }
    }

    return (
        <Suspense fallback={<p>Loading</p>}>
            <Page
                metaTitle={`${cls()?.name ?? 'Yoga Class'} — ${cls()?.startsAt ? formatDate(new Date(cls()!.startsAt), 'E dd MMM, HH:mm') : ''}`}
                metaDescription={`${cls()?.name ?? 'Yoga class'} ${cls()?.location?.name ? `at ${cls()!.location!.name}` : ''}${cls()?.location?.city ? ', ' + cls()?.location?.city : ''}. ${cls()?.startsAt ? formatDate(new Date(cls()!.startsAt), 'E dd MMM, HH:mm') + ' — ' + formatDate(new Date(cls()!.endsAt), 'HH:mm') + '.' : ''} Book your spot online.`}
            >
                <Section>
                    <Container>
                        <h1>{cls()?.name}</h1>
                        <h2 class={css.date}>{formatDate(new Date(cls()?.startsAt ?? 0), 'E dd MMM, HH:mm')} - {(formatDate(new Date(cls()?.endsAt ?? 0), 'HH:mm'))}</h2>
                        <br/>
                        <p innerHTML={cls()?.description}></p>
                        <br/>
                        <Show when={cls()?.id === "EnryXt06ZI7YUlqWIRebw"}>
                            <div class={css.images}>
                                <img src="/valentines.webp" alt={"Valentines Poster"} class={css.image}/>
                                <img src="/valentines-why.webp" alt={"Why Valentines Yoga"} class={css.image}/>
                            </div>
                        </Show>
                        <Show when={cls()?.id === "hxX1ox4y3V7YywnV-QX5p"}>
                            <div class={css.images}>
                                <img src="/summer-exhale.webp" alt={"Summer Exhale Event"} class={css.image}/>
                            </div>
                        </Show>
                    </Container>
                </Section>
                <Section>
                    <Container>
                        <h2>Location</h2>
                        <p>{cls()?.location?.name}</p>
                        <p>{cls()?.location?.address}</p>
                        <p>{cls()?.location?.city}</p>
                        <p>{cls()?.location?.state}</p>
                        <p>{cls()?.location?.postcode}</p>
                    </Container>
                </Section>
                <Section>
                    <Container>
                        <Show when={member() != null}
                              fallback={(
                                  <div>
                                      <p>Sign in or register to continue</p>
                                      <LinkButton href="/auth/sign-in" variant="primary">Sign In</LinkButton>
                                      <LinkButton href="/auth/register" variant="primarySolid">Register</LinkButton>
                                  </div>
                              )}
                        >
                            <div>
                                <p>You have {member()?.classPasses} Class Passes remaining</p>
                                <LinkButton href="/class-pass" variant="primary">Purchase Your Class Pass</LinkButton>
                                <Show when={notBookableReason()}>
                                    <p>{notBookableReason()}</p>
                                </Show>
                                <Show when={submission.error}>
                                    <p>Failed to book class, please try again</p>
                                </Show>
                                <Button onClick={book} variant="primarySolid"
                                        disabled={notBookable()}>Book
                                    Now</Button>
                            </div>
                        </Show>
                    </Container>
                </Section>
            </Page>
        </Suspense>
    )
}